import mergeClasses from '../../../utils/shallowMerge';
import defaultClasses from './Drawer.module.scss';
import DrawerCloseButton from './DrawerCloseButton';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useScrollLock } from '../../../utils/useLockScroll';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { useSelector } from 'react-redux';
import { useLayoutContext } from '../../../provider/layout/LayoutContext';

export const Drawer = ({
  children,
  onClose = () => {},
  shown = false,
  width = 0,
  height = 0,
  indent = 0,
  position = 'right',
  dataTest,
  suppressed,
  title,
  actions,
  customClasses,
  showHeader = true,
  hasOverlay = true,
  type,
  className,
  buttonCloseWidth,
  buttonCloseHeight,
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);
  const asideRef = useRef(null);
  const overlayRef = useRef(null);
  const showSearchDropdown = useSelector((state) => state['demo-ui'].showSearchDropdown);
  const { setShowLiveChat } = useLayoutContext();

  useScrollLock(shown && !showSearchDropdown);

  const [overlayShown, setOverlayShown] = useState(hasOverlay);

  useEffect(() => {
    setOverlayShown(shown);
    setShowLiveChat(false);
    return () => setShowLiveChat(true);
  }, [shown]);

  const handleOnClose = useCallback(
    (ev) => {
      if (onClose && ev.target === overlayRef.current) {
        setOverlayShown(!overlayShown);
        onClose(ev);
      }
    },
    [onClose],
  );

  const crossButtonOnClose = useCallback(() => {
    setOverlayShown(!overlayShown);
    onClose();
  }, [onClose]);

  useOutsideClick({ ref: asideRef, handler: crossButtonOnClose });

  return (
    <div
      className={clsx(styles.drawer, styles.drawerMain, className)}
      role="button"
      onClick={handleOnClose}
      data-test={dataTest}
      aria-hidden={!shown}
      ref={overlayRef}
    >
      <aside
        ref={asideRef}
        className={clsx(
          styles.aside,
          styles.asideMain,
          width && styles.customWidth,
          height && styles.customHeight,
          {
            [` ${styles.appearingFromTop}`]: position === 'top',
            [` ${styles.appearingFromBottom}`]: position === 'bottom',
            [` ${styles.appearingFromRight}`]: position === 'right',
            [` ${styles.appearingFromLeft}`]: position === 'left',
            [` ${styles.appearingFromLeft__disappearance}`]:
              !overlayShown && position === 'left',
            [` ${styles.appearingFromRight__disappearance}`]:
              !overlayShown && position === 'right',
            [` ${styles.appearingFromTop__disappearance}`]:
              !overlayShown && position === 'top',
            [` ${styles.appearingFromBottom__disappearance}`]:
              !overlayShown && position === 'bottom',
            [styles.sizeGuide]: type === 'size-guide',
          },
        )}
        style={{
          '--drawer-width': width && `${width}px`,
          '--drawer-height': height && `${height}px`,
          '--drawer-indent': indent && `${indent}px`,
        }}
        role="navigation"
        suppressed={suppressed}
      >
        <div style={{ height: '100%' }}>
          {showHeader && (title || actions || onClose) && (
            <div
              className={styles.header}
              bordered={title || actions}
              suppressed={suppressed}
            >
              {title && <h3>{title}</h3>}
              {actions && { actions }}
              <div className={styles.closeButtonWrapper}>
                {onClose && (
                  <DrawerCloseButton
                    onClick={crossButtonOnClose}
                    className={styles.crossButton}
                    variant="invisible"
                    width={buttonCloseWidth}
                    height={buttonCloseHeight}
                  />
                )}
              </div>
            </div>
          )}
          <div className={styles.content}>{children}</div>
          <footer />
        </div>
      </aside>
    </div>
  );
};

Drawer.propTypes = {
  position: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  width: PropTypes.number,
  height: PropTypes.number,
  indent: PropTypes.number,
  shown: PropTypes.bool,
};
